import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';

export default function MiniIOPEnrollment() {
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    patient_name: '', patient_age: '', guardian_name: '', phone: '', email: '',
    medical_history: '', emergency_contact: '', start_date: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/api/bookings', {
        service_type: 'mini_iop',
        patient_name: formData.patient_name,
        patient_age: parseInt(formData.patient_age),
        date: new Date(formData.start_date).toISOString(),
        notes: JSON.stringify({
          guardian_name: formData.guardian_name, phone: formData.phone,
          email: formData.email, medical_history: formData.medical_history,
          emergency_contact: formData.emergency_contact,
        }),
      });
      toast.success('Mini-IOP enrollment submitted!');
      navigate('/dashboard');
    } catch {
      toast.error('Failed to submit enrollment.');
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;
  const steps = [
    { num: 1, label: 'Patient Info' },
    { num: 2, label: 'Contact' },
    { num: 3, label: 'Medical' },
  ];

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm";

  return (
    <div className="min-h-screen pt-16 bg-[var(--bg-alt)]" data-testid="mini-iop-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[var(--primary-blue)] font-semibold mb-6 hover:gap-3 transition-all">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>

        <h2 className="text-3xl font-heading font-bold mb-2">Mini-IOP Enrollment</h2>
        <p className="text-[var(--text-body)] mb-8">Intensive Outpatient Program</p>

        {/* Progress Tracker */}
        <div className="flex items-center justify-between mb-10 relative" data-testid="progress-tracker">
          {/* Line behind */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-[var(--border-color)]" />
          <motion.div
            className="absolute top-5 left-0 h-0.5 bg-[var(--primary-blue)]"
            initial={{ width: '0%' }}
            animate={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            transition={{ duration: 0.4 }}
          />

          {steps.map((s) => (
            <div key={s.num} className="relative z-10 flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-sm transition-colors ${
                step > s.num
                  ? 'bg-[var(--primary-blue)] text-white'
                  : step === s.num
                  ? 'bg-[var(--primary-blue)] text-white ring-4 ring-blue-100'
                  : 'bg-white border-2 border-[var(--border-color)] text-[var(--text-body)]'
              }`}>
                {step > s.num ? <Check size={16} /> : s.num}
              </div>
              <span className={`text-xs mt-2 font-semibold ${step >= s.num ? 'text-[var(--primary-blue)]' : 'text-[var(--text-body)]'}`}>
                {s.label}
              </span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="card-elevated p-8">
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-1">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Patient Name *</label>
                <input name="patient_name" type="text" value={formData.patient_name} onChange={handleChange} required className={inputCls} data-testid="patient-name-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Age *</label>
                  <input name="patient_age" type="number" value={formData.patient_age} onChange={handleChange} required min="0" className={inputCls} data-testid="patient-age-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Start Date *</label>
                  <input name="start_date" type="date" value={formData.start_date} onChange={handleChange} required min={new Date().toISOString().split('T')[0]} className={inputCls} data-testid="start-date-input" />
                </div>
              </div>
              <button type="button" onClick={() => setStep(2)} className="w-full btn-primary" data-testid="next-step-1-btn">Next Step</button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-2">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Guardian Name *</label>
                <input name="guardian_name" type="text" value={formData.guardian_name} onChange={handleChange} required className={inputCls} data-testid="guardian-name-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Phone *</label>
                  <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required className={inputCls} data-testid="phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Email *</label>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} required className={inputCls} data-testid="email-input" />
                </div>
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setStep(1)} className="flex-1 btn-outline" data-testid="prev-step-2-btn">Previous</button>
                <button type="button" onClick={() => setStep(3)} className="flex-1 btn-primary" data-testid="next-step-2-btn">Next Step</button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-3">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Medical History *</label>
                <textarea name="medical_history" value={formData.medical_history} onChange={handleChange} required rows="4"
                  className={inputCls + " resize-none"} placeholder="Relevant medical history" data-testid="medical-history-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Emergency Contact *</label>
                <input name="emergency_contact" type="text" value={formData.emergency_contact} onChange={handleChange} required
                  className={inputCls} placeholder="Name and phone" data-testid="emergency-contact-input" />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setStep(2)} className="flex-1 btn-outline" data-testid="prev-step-3-btn">Previous</button>
                <button type="submit" disabled={loading} className="flex-1 btn-primary disabled:opacity-50" data-testid="submit-enrollment-btn">
                  {loading ? 'Submitting...' : 'Submit Enrollment'}
                </button>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
}
